import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  allowCreate?: boolean;
  placeholder?: string;
}

interface TagOption {
  tag: string;
  isNew: boolean;
}

const MAX_LENGTH = 64;

export function TagInput({
  value,
  onChange,
  suggestions,
  allowCreate = true,
  placeholder,
}: TagInputProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const query = text.trim().toLowerCase();
  const selected = useMemo(() => new Set(value.map((tag) => tag.toLowerCase())), [value]);

  const options = useMemo<TagOption[]>(() => {
    const matches: TagOption[] = suggestions
      .filter((tag) => !selected.has(tag.toLowerCase()))
      .filter((tag) => !query || tag.toLowerCase().includes(query))
      .map((tag) => ({ tag, isNew: false }));
    const known =
      selected.has(query) || suggestions.some((tag) => tag.toLowerCase() === query);
    if (allowCreate && query && !known) {
      matches.push({ tag: text.trim(), isNew: true });
    }
    return matches;
  }, [suggestions, selected, query, text, allowCreate]);

  const showList = open && options.length > 0;
  const active = Math.min(highlight, Math.max(options.length - 1, 0));

  function add(raw: string) {
    const tag = raw.trim().slice(0, MAX_LENGTH);
    if (!tag) {
      return;
    }
    const key = tag.toLowerCase();
    const existing = suggestions.find((candidate) => candidate.toLowerCase() === key);
    if (!existing && !allowCreate) {
      return;
    }
    if (!selected.has(key)) {
      onChange([...value, existing ?? tag]);
    }
    setText('');
    setHighlight(0);
  }

  function remove(tag: string) {
    onChange(value.filter((candidate) => candidate !== tag));
  }

  function move(step: number) {
    setOpen(true);
    if (options.length > 0) {
      setHighlight((active + step + options.length) % options.length);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Enter') {
      const option = showList ? options[active] : undefined;
      if (option) {
        event.preventDefault();
        add(option.tag);
      } else if (query) {
        event.preventDefault();
        add(text);
      }
    } else if (event.key === ',') {
      event.preventDefault();
      add(text);
    } else if (event.key === 'Backspace' && text === '' && value.length > 0) {
      remove(value[value.length - 1]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="tag-input-wrap">
      <div className="tag-input" onClick={() => inputRef.current?.focus()}>
        {value.map((tag) => (
          <span key={tag} className="tag-pill">
            {tag}
            <button
              type="button"
              className="tag-pill-remove"
              aria-label={`Remove ${tag}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                remove(tag);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tag-input-field"
          value={text}
          maxLength={MAX_LENGTH}
          placeholder={value.length === 0 ? placeholder : undefined}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(event) => {
            setText(event.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            add(text);
            setText('');
            setOpen(false);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {showList && (
        <ul className="tag-suggest" id={listId} role="listbox">
          {options.map((option, index) => (
            <li
              key={`${option.isNew ? 'new' : 'tag'}-${option.tag}`}
              role="option"
              aria-selected={index === active}
              className={`tag-suggest-item${index === active ? ' active' : ''}`}
              onMouseDown={(event) => {
                event.preventDefault();
                add(option.tag);
              }}
              onMouseEnter={() => setHighlight(index)}
            >
              {option.isNew ? (
                <>
                  Add “<strong>{option.tag}</strong>”
                </>
              ) : (
                option.tag
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
